import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import {
	AlertTriangle,
	ArrowLeft,
	Calendar,
	ChevronRight,
	Play,
	RefreshCw,
	Trash2,
	Trophy,
	UserX,
} from "lucide-react";
import { useState } from "react";
import { useConfirm } from "@/components/ConfirmDialog";
import { getDb } from "@/db";
import {
	cars,
	events,
	heatAssignments,
	heats,
	laneRecords,
	runs,
} from "@/db/schema";
import { getSession } from "@/lib/auth-session";
import { generateRemainingSchedule, generateSchedule } from "@/lib/scheduling";
import { computeScores } from "@/lib/scoring";

async function requireAdmin() {
	const session = await getSession();
	if (!session?.user) throw new Error("Unauthorized");
	const role = (session.user as { role?: string }).role;
	if (role !== "admin" && role !== "operator") throw new Error("Forbidden");
}

// ── Server functions ────────────────────────────────────────────────

const getScheduleData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));

		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, event.id));

		// Get all assignments for all heats
		let assignments: Array<{
			id: number;
			heatId: number;
			lane: number;
			carId: number;
		}> = [];
		if (eventHeats.length > 0) {
			for (const heatId of eventHeats.map((h) => h.id)) {
				const ha = await db
					.select()
					.from(heatAssignments)
					.where(eq(heatAssignments.heatId, heatId));
				assignments = assignments.concat(ha);
			}
		}

		return { event, cars: eventCars, heats: eventHeats, assignments };
	});

const generateAndSaveSchedule = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; division?: string | null }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, data.eventId))
			.get();
		if (!event) throw new Error("Event not found");

		// Get eligible cars — if requireCheckin is set, only include checked-in cars
		let eligibleCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, data.eventId));

		if (data.division) {
			eligibleCars = eligibleCars.filter((c) => c.division === data.division);
		}

		const checkedIn = eligibleCars.filter((c) => c.checkedIn);
		const scheduleCars = event.requireCheckin
			? checkedIn
			: checkedIn.length > 0
				? checkedIn
				: eligibleCars;

		if (scheduleCars.length === 0) throw new Error("No eligible cars");

		// Delete existing heats for this division
		const existingHeats = await db
			.select()
			.from(heats)
			.where(
				data.division
					? and(
							eq(heats.eventId, data.eventId),
							eq(heats.division, data.division),
						)
					: and(eq(heats.eventId, data.eventId)),
			);

		for (const h of existingHeats) {
			if (data.division && h.division !== data.division) continue;
			if (!data.division && h.division != null) continue;
			await db.delete(runs).where(eq(runs.heatId, h.id));
			await db.delete(heatAssignments).where(eq(heatAssignments.heatId, h.id));
			await db.delete(heats).where(eq(heats.id, h.id));
		}

		// Clear lane records for this division
		if (data.division) {
			await db
				.delete(laneRecords)
				.where(
					and(
						eq(laneRecords.eventId, data.eventId),
						eq(laneRecords.division, data.division),
					),
				);
		} else {
			await db
				.delete(laneRecords)
				.where(eq(laneRecords.eventId, data.eventId));
		}

		// Generate schedule with rounds (and optional partial schedule)
		const schedule = generateSchedule(
			scheduleCars.map((c) => c.id),
			event.laneCount,
			event.rounds,
			event.racesPerCar ?? undefined,
		);

		// Group by heat number and save
		const heatMap = new Map<number, Array<{ lane: number; carId: number }>>();
		for (const a of schedule) {
			const group = heatMap.get(a.heatNumber) || [];
			group.push({ lane: a.lane, carId: a.carId });
			heatMap.set(a.heatNumber, group);
		}

		for (const [heatNumber, lanes] of heatMap) {
			const [heat] = await db
				.insert(heats)
				.values({
					eventId: data.eventId,
					heatNumber,
					division: data.division ?? null,
				})
				.returning();

			for (const lane of lanes) {
				await db.insert(heatAssignments).values({
					heatId: heat.id,
					lane: lane.lane,
					carId: lane.carId,
				});
			}
		}

		return { heatCount: heatMap.size, carCount: scheduleCars.length };
	});

const generateFinalsSchedule = createServerFn({ method: "POST" })
	.inputValidator((data: { eventId: number; division?: string | null }) => data)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, data.eventId))
			.get();
		if (!event) throw new Error("Event not found");
		if (!event.finalsCarCount || event.finalsCarCount < 2) {
			throw new Error("Finals car count must be set (at least 2)");
		}

		// Get qualifying scores to determine top N cars
		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, data.eventId));

		const eventRuns = await db
			.select()
			.from(runs)
			.where(and(eq(runs.eventId, data.eventId), eq(runs.status, "valid")));

		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, data.eventId));

		const allAssignments: Array<{
			heatId: number;
			lane: number;
			carId: number;
		}> = [];
		for (const heat of eventHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, heat.id));
			allAssignments.push(...ha);
		}

		// Filter cars by division if specified
		const scoreCars = data.division
			? eventCars.filter((c) => c.division === data.division)
			: eventCars;

		const scores = computeScores(
			scoreCars.map((c) => ({
				id: c.id,
				carNumber: c.carNumber,
				carName: c.carName,
				driverName: c.driverName,
				avatarUrl: c.avatarUrl,
				photoUrl: c.photoUrl,
				division: c.division,
			})),
			eventRuns.map((r) => ({
				carId: r.carId,
				elapsedTimeMs: r.elapsedTimeMs,
				lane: r.lane,
				heatId: r.heatId,
			})),
			event.scoringMethod,
			{
				heats: eventHeats.map((h) => ({
					id: h.id,
					status: h.status,
					phase: h.phase,
				})),
				assignments: allAssignments,
				laneCount: event.laneCount,
				phase: "qualifying",
			},
		);

		// Take top N cars with scores
		const topCars = scores
			.filter((s) => s.score != null)
			.slice(0, event.finalsCarCount);

		if (topCars.length < 2) {
			throw new Error("Not enough cars with scores for finals");
		}

		// Delete existing finals heats for this division
		const finalsHeats = eventHeats.filter((h) => {
			if (h.phase !== "finals") return false;
			if (data.division) return h.division === data.division;
			return h.division == null;
		});
		for (const h of finalsHeats) {
			await db.delete(heatAssignments).where(eq(heatAssignments.heatId, h.id));
			await db.delete(heats).where(eq(heats.id, h.id));
		}

		// Generate finals schedule
		const finalsRounds = event.finalsRounds ?? 1;
		const schedule = generateSchedule(
			topCars.map((s) => s.car.id),
			event.laneCount,
			finalsRounds,
		);

		// Determine starting heat number (after all existing heats, excluding deleted finals)
		const remainingHeats = eventHeats.filter(
			(h) => !finalsHeats.some((fh) => fh.id === h.id),
		);
		const maxHeatNum =
			remainingHeats.length > 0
				? Math.max(...remainingHeats.map((h) => h.heatNumber))
				: 0;

		// Group and save
		const heatMap = new Map<number, Array<{ lane: number; carId: number }>>();
		for (const a of schedule) {
			const group = heatMap.get(a.heatNumber) || [];
			group.push({ lane: a.lane, carId: a.carId });
			heatMap.set(a.heatNumber, group);
		}

		let heatIdx = 0;
		for (const [, lanes] of heatMap) {
			heatIdx++;
			const [heat] = await db
				.insert(heats)
				.values({
					eventId: data.eventId,
					heatNumber: maxHeatNum + heatIdx,
					phase: "finals",
					division: data.division ?? null,
				})
				.returning();

			for (const lane of lanes) {
				await db.insert(heatAssignments).values({
					heatId: heat.id,
					lane: lane.lane,
					carId: lane.carId,
				});
			}
		}

		return {
			heatCount: heatMap.size,
			carCount: topCars.length,
			advancedCars: topCars.map((s) => ({
				id: s.car.id,
				carName: s.car.carName,
				carNumber: s.car.carNumber,
			})),
		};
	});

const removeNoShow = createServerFn({ method: "POST" })
	.inputValidator(
		(data: { eventId: number; carId: number; division?: string | null }) =>
			data,
	)
	.handler(async ({ data }) => {
		await requireAdmin();
		const db = getDb();

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, data.eventId))
			.get();
		if (!event) throw new Error("Event not found");

		// Get all heats for this event/division
		const allHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, data.eventId));

		const divisionHeats = allHeats.filter((h) => {
			if (data.division) return h.division === data.division;
			return h.division == null;
		});

		const scheduledHeats = divisionHeats.filter(
			(h) => h.status === "scheduled",
		);
		const completedHeats = divisionHeats.filter((h) => h.status === "complete");

		if (scheduledHeats.length === 0) {
			throw new Error("No scheduled heats to modify");
		}

		// Get all assignments
		let allAssignments: Array<{
			id: number;
			heatId: number;
			lane: number;
			carId: number;
		}> = [];
		for (const h of divisionHeats) {
			const ha = await db
				.select()
				.from(heatAssignments)
				.where(eq(heatAssignments.heatId, h.id));
			allAssignments = allAssignments.concat(ha);
		}

		// Figure out which cars are still active (excluding the no-show)
		const activeCarIds = new Set<number>();
		for (const a of allAssignments) {
			if (a.carId !== data.carId) {
				activeCarIds.add(a.carId);
			}
		}
		const activeCarIdArray = [...activeCarIds];

		// Build completed lanes map for active cars
		const completedHeatIds = new Set(completedHeats.map((h) => h.id));
		const completedLanes = new Map<number, number[]>();
		for (const a of allAssignments) {
			if (a.carId === data.carId) continue;
			if (!completedHeatIds.has(a.heatId)) continue;
			const lanes = completedLanes.get(a.carId) || [];
			lanes.push(a.lane);
			completedLanes.set(a.carId, lanes);
		}

		// Delete all scheduled heats
		for (const h of scheduledHeats) {
			await db.delete(heatAssignments).where(eq(heatAssignments.heatId, h.id));
			await db.delete(heats).where(eq(heats.id, h.id));
		}

		// Determine next heat number
		const maxCompletedHeatNum =
			completedHeats.length > 0
				? Math.max(...completedHeats.map((h) => h.heatNumber))
				: 0;

		// Generate remaining schedule
		const remaining = generateRemainingSchedule(
			activeCarIdArray,
			event.laneCount,
			event.rounds,
			completedLanes,
			maxCompletedHeatNum + 1,
			event.racesPerCar ?? undefined,
		);

		// Group and insert
		const heatMap = new Map<number, Array<{ lane: number; carId: number }>>();
		for (const a of remaining) {
			const group = heatMap.get(a.heatNumber) || [];
			group.push({ lane: a.lane, carId: a.carId });
			heatMap.set(a.heatNumber, group);
		}

		for (const [heatNumber, lanes] of heatMap) {
			const [heat] = await db
				.insert(heats)
				.values({
					eventId: data.eventId,
					heatNumber,
					division: data.division ?? null,
				})
				.returning();

			for (const lane of lanes) {
				await db.insert(heatAssignments).values({
					heatId: heat.id,
					lane: lane.lane,
					carId: lane.carId,
				});
			}
		}

		// Also mark the car as not checked in
		await db
			.update(cars)
			.set({ checkedIn: false })
			.where(eq(cars.id, data.carId));

		return { removedCarId: data.carId, newHeatCount: heatMap.size };
	});

const deleteSchedule = createServerFn({ method: "POST" })
	.inputValidator((eventId: number) => {
		if (!Number.isInteger(eventId) || eventId <= 0)
			throw new Error("Invalid eventId");
		return eventId;
	})
	.handler(async ({ data: eventId }) => {
		await requireAdmin();
		const db = getDb();
		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, eventId));

		for (const h of eventHeats) {
			await db.delete(runs).where(eq(runs.heatId, h.id));
			await db.delete(heatAssignments).where(eq(heatAssignments.heatId, h.id));
		}
		await db.delete(heats).where(eq(heats.eventId, eventId));
		await db.delete(laneRecords).where(eq(laneRecords.eventId, eventId));
	});

// ── Route ───────────────────────────────────────────────────────────

export const Route = createFileRoute("/admin/events/$eventId/schedule")({
	loader: ({ params }) => getScheduleData({ data: params.eventId }),
	component: SchedulePage,
});

function SchedulePage() {
	const params = Route.useParams();
	const initialData = Route.useLoaderData();
	const queryClient = useQueryClient();
	const [confirm, confirmDialog] = useConfirm();

	const { data } = useQuery({
		queryKey: ["schedule", params.eventId],
		queryFn: () => getScheduleData({ data: params.eventId }),
		initialData,
		refetchInterval: 5000,
	});

	const { event, cars: carList, heats: eventHeats, assignments } = data;

	const divisions: string[] = (() => {
		try {
			return JSON.parse(event.divisions);
		} catch {
			return [];
		}
	})();

	const [generating, setGenerating] = useState(false);
	const [generatingFinals, setGeneratingFinals] = useState(false);
	const [selectedDivision, setSelectedDivision] = useState<string | null>(null);
	const [showNoShow, setShowNoShow] = useState(false);
	const [removingCar, setRemovingCar] = useState<number | null>(null);
	const [selectedPhase, setSelectedPhase] = useState<
		"qualifying" | "finals" | null
	>(null);

	const invalidate = () =>
		queryClient.invalidateQueries({
			queryKey: ["schedule", params.eventId],
		});

	const hasFinalsHeats = eventHeats.some((h) => h.phase === "finals");

	// Filter heats by selected division and phase
	const filteredHeats = eventHeats
		.filter((h) => {
			if (selectedDivision !== null && h.division !== selectedDivision)
				return false;
			if (selectedPhase !== null && h.phase !== selectedPhase) return false;
			return true;
		})
		.sort((a, b) => a.heatNumber - b.heatNumber);

	const nextScheduledHeat = filteredHeats.find((h) => h.status === "scheduled");
	const completedCount = filteredHeats.filter(
		(h) => h.status === "complete",
	).length;
	const hasScheduledHeats = filteredHeats.some((h) => h.status === "scheduled");

	// Cars that are in scheduled heats (candidates for no-show removal)
	const scheduledHeatIds = new Set(
		filteredHeats.filter((h) => h.status === "scheduled").map((h) => h.id),
	);
	const carsInScheduledHeats = new Set(
		assignments
			.filter((a) => scheduledHeatIds.has(a.heatId))
			.map((a) => a.carId),
	);
	const noShowCandidates = carList.filter((c) =>
		carsInScheduledHeats.has(c.id),
	);

	async function handleGenerate() {
		setGenerating(true);
		try {
			if (selectedDivision === null && divisions.length > 0) {
				for (const div of divisions) {
					await generateAndSaveSchedule({
						data: { eventId: event.id, division: div },
					});
				}
			} else {
				await generateAndSaveSchedule({
					data: { eventId: event.id, division: selectedDivision },
				});
			}
			invalidate();
		} catch (err) {
			console.error("Failed to generate schedule:", err);
		} finally {
			setGenerating(false);
		}
	}

	async function handleGenerateFinals() {
		const divisionLabel =
			divisions.length > 0
				? ` for each division (${divisions.join(", ")})`
				: "";
		const ok = await confirm({
			title: "Generate Finals Schedule",
			description: `This will generate a finals schedule for the top ${event.finalsCarCount ?? "N"} qualifying cars${divisionLabel}. Any existing finals heats will be replaced.`,
			confirmLabel: "Generate Finals",
		});
		if (!ok) return;
		setGeneratingFinals(true);
		try {
			if (divisions.length > 0) {
				for (const div of divisions) {
					await generateFinalsSchedule({
						data: { eventId: event.id, division: div },
					});
				}
			} else {
				await generateFinalsSchedule({ data: { eventId: event.id } });
			}
			setSelectedPhase("finals");
			invalidate();
		} catch (err) {
			console.error("Failed to generate finals:", err);
			alert(err instanceof Error ? err.message : "Failed to generate finals");
		} finally {
			setGeneratingFinals(false);
		}
	}

	async function handleNoShow(carId: number) {
		const car = carList.find((c) => c.id === carId);
		const ok = await confirm({
			title: "Remove No-Show Car",
			description: `Remove ${car?.carName || car?.driverName} from all remaining heats? Their completed runs will be kept.`,
			confirmLabel: "Remove",
			variant: "destructive",
		});
		if (!ok) return;

		setRemovingCar(carId);
		try {
			await removeNoShow({
				data: {
					eventId: event.id,
					carId,
					division: selectedDivision,
				},
			});
			invalidate();
		} catch (err) {
			console.error("Failed to remove no-show:", err);
		} finally {
			setRemovingCar(null);
		}
	}

	async function handleDelete() {
		const ok = await confirm({
			title: "Delete Schedule",
			description:
				"Delete the entire schedule including all race results? This cannot be undone.",
			confirmLabel: "Delete",
			variant: "destructive",
		});
		if (!ok) return;
		await deleteSchedule({ data: event.id });
		invalidate();
	}

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			{confirmDialog}
			<div className="max-w-3xl mx-auto">
				<Link
					to="/admin/events/$eventId"
					params={{ eventId: String(event.id) }}
					className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground text-sm mb-4 transition-colors"
				>
					<ArrowLeft size={16} />
					Back to {event.name}
				</Link>

				<h1 className="text-4xl text-foreground mb-2">Race Schedule</h1>
				<p className="text-muted-foreground mb-6">{event.name}</p>

				{/* Division tabs */}
				{divisions.length > 0 && (
					<div className="flex gap-2 mb-6 flex-wrap">
						<button
							type="button"
							onClick={() => setSelectedDivision(null)}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								selectedDivision === null
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:text-foreground"
							}`}
						>
							All
						</button>
						{divisions.map((div) => (
							<button
								key={div}
								type="button"
								onClick={() => setSelectedDivision(div)}
								className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
									selectedDivision === div
										? "bg-primary text-primary-foreground"
										: "bg-muted text-muted-foreground hover:text-foreground"
								}`}
							>
								{div}
							</button>
						))}
					</div>
				)}

				{/* Phase tabs */}
				{hasFinalsHeats && (
					<div className="flex gap-2 mb-4 flex-wrap">
						<button
							type="button"
							onClick={() => setSelectedPhase(null)}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								selectedPhase === null
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:text-foreground"
							}`}
						>
							All
						</button>
						<button
							type="button"
							onClick={() => setSelectedPhase("qualifying")}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								selectedPhase === "qualifying"
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:text-foreground"
							}`}
						>
							Qualifying
						</button>
						<button
							type="button"
							onClick={() => setSelectedPhase("finals")}
							className={`px-4 py-1.5 rounded-full text-sm font-bold transition-colors ${
								selectedPhase === "finals"
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:text-foreground"
							}`}
						>
							Finals
						</button>
					</div>
				)}

				{/* Generate controls */}
				<div className="bg-card rounded-xl p-4 sm:p-6 mb-6">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-foreground font-bold">
								{filteredHeats.length === 0
									? "No schedule generated"
									: `${filteredHeats.length} heats (${completedCount} complete)`}
							</p>
							<p className="text-muted-foreground text-sm">
								{carList.filter((c) => c.checkedIn).length} of {carList.length}{" "}
								cars checked in
								{" \u2022 "}
								{event.laneCount} lanes
								{event.racesPerCar != null && (
									<>
										{" \u2022 "}
										{event.racesPerCar} races/car
									</>
								)}
								{" \u2022 "}
								{event.rounds} round{event.rounds > 1 ? "s" : ""}
							</p>
							{filteredHeats.length > 0 && (
								<div className="flex items-center gap-2 mt-2">
									<div className="flex-1 max-w-48 h-2 bg-muted rounded-full overflow-hidden">
										<div
											className="h-full bg-success rounded-full transition-all"
											style={{
												width: `${Math.round((completedCount / filteredHeats.length) * 100)}%`,
											}}
										/>
									</div>
									<span className="text-xs text-muted-foreground font-bold">
										{Math.round((completedCount / filteredHeats.length) * 100)}%
									</span>
								</div>
							)}
						</div>
						<div className="flex items-center gap-2">
							{hasScheduledHeats && (
								<button
									type="button"
									onClick={() => setShowNoShow(!showNoShow)}
									title="Remove a car that didn't show up and regenerate remaining heats"
									className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg transition-colors text-sm font-bold ${
										showNoShow
											? "bg-status-finals/20 text-status-finals"
											: "bg-muted text-muted-foreground hover:text-foreground"
									}`}
								>
									<UserX size={14} />
									No-Show
								</button>
							)}
							{filteredHeats.length > 0 && (
								<button
									type="button"
									onClick={handleDelete}
									title="Delete the entire schedule, results, and lane records"
									className="inline-flex items-center gap-1.5 px-3 py-2 bg-destructive/10 hover:bg-destructive/20 text-destructive rounded-lg transition-colors text-sm font-bold"
								>
									<Trash2 size={14} />
									Clear
								</button>
							)}
							<button
								type="button"
								onClick={handleGenerate}
								disabled={generating}
								title={filteredHeats.length === 0
									? "Create heat assignments ensuring each car races in every lane"
									: "Replace the current schedule with a new one (completed heats are preserved)"}
								className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-accent disabled:opacity-50 text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
							>
								{generating ? (
									<RefreshCw size={14} className="animate-spin" />
								) : (
									<Calendar size={14} />
								)}
								{filteredHeats.length === 0
									? "Generate Schedule"
									: "Regenerate"}
							</button>
							{event.finalsCarCount != null &&
								event.finalsCarCount > 0 &&
								(event.status === "qualifying" ||
									event.status === "finals") && (
									<button
										type="button"
										onClick={handleGenerateFinals}
										disabled={generatingFinals}
										title={`Create finals heats for the top ${event.finalsCarCount ?? "N"} qualifying cars`}
										className="inline-flex items-center gap-1.5 px-4 py-2 bg-status-finals hover:bg-status-finals/80 disabled:opacity-50 text-primary-foreground rounded-lg transition-colors text-sm font-bold uppercase"
									>
										{generatingFinals ? (
											<RefreshCw size={14} className="animate-spin" />
										) : (
											<Trophy size={14} />
										)}
										Generate Finals
									</button>
								)}
						</div>
					</div>
				</div>

				{/* Check-in warning */}
				{event.requireCheckin && carList.some((c) => !c.checkedIn) && (
					<div className="bg-status-finals/10 border border-status-finals/30 rounded-xl p-4 mb-6 flex items-start gap-3">
						<AlertTriangle
							size={18}
							className="text-status-finals shrink-0 mt-0.5"
						/>
						<div>
							<p className="text-foreground font-bold text-sm">
								Check-in required
							</p>
							<p className="text-muted-foreground text-sm">
								{carList.filter((c) => !c.checkedIn).length} car
								{carList.filter((c) => !c.checkedIn).length !== 1 ? "s" : ""}{" "}
								not checked in and will be excluded from the schedule.
							</p>
						</div>
					</div>
				)}

				{/* No-show panel */}
				{showNoShow && noShowCandidates.length > 0 && (
					<div className="bg-status-finals/5 border border-status-finals/30 rounded-xl p-4 sm:p-6 mb-6">
						<h3 className="text-foreground font-bold mb-1">Mark No-Show</h3>
						<p className="text-muted-foreground text-sm mb-3">
							Remove a car from all remaining heats. Completed runs are
							preserved. Remaining heats will be regenerated to fill empty
							lanes.
						</p>
						<div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
							{noShowCandidates
								.sort((a, b) => a.carNumber - b.carNumber)
								.map((car) => (
									<button
										key={car.id}
										type="button"
										onClick={() => handleNoShow(car.id)}
										disabled={removingCar === car.id}
										className="flex items-center gap-2 px-3 py-2 bg-card hover:bg-destructive/10 border border-border hover:border-destructive/50 rounded-lg transition-colors text-sm text-left disabled:opacity-50"
									>
										<UserX
											size={14}
											className="text-muted-foreground shrink-0"
										/>
										<div className="min-w-0">
											<p className="text-foreground font-bold truncate">
												<span className="text-primary text-xs mr-1">
													#{car.carNumber}
												</span>
												{car.carName}
											</p>
											<p className="text-muted-foreground text-xs truncate">
												{car.driverName}
											</p>
										</div>
									</button>
								))}
						</div>
					</div>
				)}

				{/* Heat list */}
				{filteredHeats.length > 0 && (
					<div className="space-y-3">
						{filteredHeats.map((heat) => {
							const heatAssigns = assignments.filter(
								(a) => a.heatId === heat.id,
							);
							const isNext = heat.id === nextScheduledHeat?.id;

							return (
								<div
									key={heat.id}
									className={`bg-card/50 rounded-xl border-2 p-4 transition-all ${
										heat.status === "complete"
											? "border-success/30 opacity-70"
											: isNext
												? "border-primary"
												: "border-border"
									}`}
								>
									<div className="flex items-center justify-between mb-3">
										<div className="flex items-center gap-2">
											<span className="text-foreground font-bold">
												Heat #{heat.heatNumber}
											</span>
											{heat.phase === "finals" && (
												<span className="px-2 py-0.5 bg-status-finals/20 text-status-finals text-xs font-bold rounded uppercase">
													Finals
												</span>
											)}
											{heat.division && (
												<span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-bold rounded">
													{heat.division}
												</span>
											)}
											<span
												className={`px-2 py-0.5 text-xs font-bold rounded uppercase ${
													heat.status === "complete"
														? "bg-success/20 text-success"
														: heat.status === "racing"
															? "bg-primary/20 text-primary"
															: "bg-muted text-muted-foreground"
												}`}
											>
												{heat.status}
											</span>
										</div>
										{isNext && (
											<Link
												to="/admin/events/$eventId/race"
												params={{
													eventId: String(event.id),
												}}
												className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary hover:bg-accent text-primary-foreground rounded-lg transition-colors text-sm font-bold"
											>
												<Play size={12} />
												Race
												<ChevronRight size={14} />
											</Link>
										)}
									</div>
									<div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
										{heatAssigns
											.sort((a, b) => a.lane - b.lane)
											.map((assign) => {
												const car = carList.find((c) => c.id === assign.carId);
												return (
													<div
														key={assign.id}
														className="bg-background rounded-lg px-3 py-2 text-sm"
													>
														<span className="text-muted-foreground text-xs font-bold">
															Lane {assign.lane}
														</span>
														<p className="text-foreground font-bold truncate">
															<span className="text-primary text-xs mr-1">
																#{car?.carNumber}
															</span>
															{car?.carName ?? car?.driverName}
														</p>
													</div>
												);
											})}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
